/**
 * PaperCardList — 좁은 시험지 목록의 공용 카드·로딩·페이지 이동 표시.
 *
 * 화면별 API와 행동은 담는 쪽이 쥔다. 관리 화면에서는 AI 복사 행동을 넘기고,
 * 학생 허브 선택창에서는 넘기지 않아 같은 카드에 관리 행동이 섞이지 않는다.
 */
import React from 'react';
import { Copy, Eye, Loader2, Sparkles } from 'lucide-react';
// 배럴(`../common`)이 아니라 부품 파일을 직접 가져온다 — 배럴을 타면 Header→axios 까지 딸려와
// 이 파일을 쓰는 시험(PaperListShared.test.jsx)이 ESM 파싱에서 멈춘다. 렌더 결과는 같다.
import { DesktopPagination } from '../common/DesktopPagination';
import { fmtPaperDate, paperAuthorName, paperIdOf, paperItemCount } from './PaperTable';

export function PaperCardList({
  items = [],
  selectedId = null,
  onSelect,
  onPreview = null,
  previewBusyId = null,
  onAiCopy = null,
  selectionClassName = '',
}) {
  return (
    <ul className="space-y-1">
      {items.map((paper) => {
        const id = paperIdOf(paper);
        const active = String(id) === String(selectedId);
        const count = paperItemCount(paper);
        const previewPending = previewBusyId != null;
        const previewBusyHere = previewPending && String(previewBusyId) === String(id);
        return (
          <li key={id}>
            <div
              className={`rounded-xl border p-2 transition-colors ${
                active ? 'border-primary/40 bg-primary/5' : 'border-transparent hover:bg-gray-50'
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(id)}
                aria-pressed={active}
                className={`w-full text-left ${selectionClassName}`.trim()}
              >
                <div className="flex items-center gap-1.5">
                  <span className="shrink-0 font-mono text-[10px] text-gray-400">#{id}</span>
                  {paper.is_ai_copy && (
                    <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-medium text-violet-700">
                      <Sparkles aria-hidden="true" className="size-2.5" />
                      AI 복사
                    </span>
                  )}
                  {paper.revision != null && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] text-gray-600">rev {paper.revision}</span>
                  )}
                  <span className="ml-auto shrink-0 text-[10px] text-gray-400">
                    {fmtPaperDate(paper.updated_at || paper.created_at)}
                  </span>
                </div>
                <p className="mt-1 truncate text-sm font-medium text-gray-800">
                  {paper.title || <span className="text-gray-300">제목 없음</span>}
                </p>
                <p className="mt-0.5 break-words text-[10px] text-gray-400">
                  문항 {paperItemCount(paper) ?? '—'}개
                  {paperAuthorName(paper) ? ` · ${paperAuthorName(paper)}` : ''}
                  {paper.derived_from != null ? ` · 원본 #${paper.derived_from}` : ''}
                  {` · ${paper.folder_name || '폴더 없음'}`}
                </p>
              </button>
              {(typeof onPreview === 'function' || typeof onAiCopy === 'function') && (
                <div className="mt-1.5 flex items-center gap-1">
                  {typeof onPreview === 'function' && (
                    <button
                      type="button"
                      onClick={() => onPreview(paper)}
                      disabled={previewPending || count === 0}
                      aria-busy={previewBusyHere || undefined}
                      aria-label={`${paper.title || `#${id}`} 미리보기`}
                      title={count === 0
                        ? '문항이 없어 미리볼 수 없습니다'
                        : previewBusyHere
                          ? '인쇄 미리보기를 준비하고 있습니다'
                          : previewPending
                            ? '다른 시험지의 인쇄 미리보기를 준비하고 있습니다'
                            : '미리보기'}
                      className="inline-flex items-center gap-0.5 rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {previewBusyHere
                        ? <Loader2 aria-hidden="true" className="size-2.5 animate-spin" />
                        : <Eye aria-hidden="true" className="size-2.5" />}
                      미리보기
                    </button>
                  )}
                  {typeof onAiCopy === 'function' && (
                    <button
                      type="button"
                      onClick={() => onAiCopy(paper)}
                      aria-label={`${paper.title || `#${id}`} AI 복사`}
                      className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-100"
                    >
                      <Copy aria-hidden="true" className="size-2.5" />
                      AI 복사
                    </button>
                  )}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function PaperListSkeleton() {
  return (
    <div className="space-y-2 p-2" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((index) => (
        <div key={index} className="h-14 rounded-xl bg-gray-100 motion-safe:animate-pulse" />
      ))}
    </div>
  );
}

/**
 * 시험지 목록의 페이지 이동 — 이 저장소의 정본 부품(DesktopPagination)의 compact 변형이다.
 *
 * 종전에는 [◀] 1/2 [▶] 뿐이라 한 번에 한 칸씩만 갈 수 있었다. 이제 번호를 눌러 뛰고,
 * 현재 칸에 숫자를 쳐서(Enter) 바로 갈 수도 있다. 조건·값은 형제 두 화면과 같다
 * (ProblemHubV2/ProblemSearchPanel · ExamPapersV2/PaperComposeModal).
 *
 * compact 는 한 단 작은 변형이다(32px·12px). 기본값 40px 은 페이지 전체를 쓰는 목록용이라
 * 이 패널 안에서는 표보다 커 보인다.
 *
 * 읽는 동안 연타를 막는다 — 부품은 loading 을 모르므로 여기서 가둔다.
 */
export function PaperListPagination({ page, totalPages, loading = false, onPageChange, style }) {
  if (totalPages <= 1) return null;

  return (
    <div className="mt-2 shrink-0" style={style}>
      <DesktopPagination
        compact
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(nextPage) => { if (!loading) onPageChange(nextPage); }}
      />
    </div>
  );
}
